import { useEffect, useMemo, useState } from 'react'

/*
  Figma: node 260:3732 (Contact / Thank You) — https://www.figma.com/design/d8Jwy74NxvTumtVqSNQxVr/Migration-Galleries?node-id=260-3732
  Same shell as the Contact page, but the form fields display the submitter's
  values as read-only confirmation text, and "Thanks for your message, we'll be
  in touch soon" appears alongside an inert Send button.

  Submitted values come from sessionStorage (written by Contact.tsx just before
  the redirect). If someone lands here directly without a stashed submission,
  we still render the confirmation message and an empty form outline.

  Styling lives in src/styles/pages/contact-thank-you.css.
*/

interface SubmittedValues {
  name: string
  email: string
  phone: string
  message: string
}

const FIELD_ORDER: Array<{ key: keyof SubmittedValues; label: string; multiline?: boolean }> = [
  { key: 'name', label: 'NAME' },
  { key: 'email', label: 'EMAIL ADDRESS' },
  { key: 'phone', label: 'PHONE NUMBER' },
  { key: 'message', label: 'MESSAGE', multiline: true },
]

function readSubmittedValues(): SubmittedValues | null {
  try {
    const raw = sessionStorage.getItem('mig-contact-submission')
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    return {
      name: typeof parsed.name === 'string' ? parsed.name : '',
      email: typeof parsed.email === 'string' ? parsed.email : '',
      phone: typeof parsed.phone === 'string' ? parsed.phone : '',
      message: typeof parsed.message === 'string' ? parsed.message : '',
    }
  } catch {
    return null
  }
}

export default function ContactThankYou() {
  const [visible, setVisible] = useState(false)
  const submitted = useMemo(readSubmittedValues, [])

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(timer)
  }, [])

  return (
    <section className="mig-page-section">
      <div className="mig-page-title-row" data-visible={visible}>
        <h1 className="mig-contact-page-title">Contact</h1>
      </div>

      <div className="mig-contact-form-wrapper">
        {/* Read-only form fields showing submitted values */}
        <div className="mig-contact-form-fields">
          {FIELD_ORDER.map(({ key, label, multiline }) => (
            <ReadOnlyFieldRow
              key={key}
              label={label}
              value={submitted?.[key] ?? ''}
              multiline={multiline}
            />
          ))}
        </div>

        {/* Thank-you message + (inert) Send button row */}
        <div className="mig-contact-submit-row" data-layout="thankyou">
          <p className="mig-contact-thankyou-message">
            Thanks for your message, we&apos;ll be in touch soon
          </p>

          <span aria-hidden="true" className="mig-contact-submit-btn">Send</span>
        </div>

        {/* BACK button - links to homepage */}
        <div className="mig-page-back-row">
          <a href="/" className="mig-contact-back-button">BACK</a>
          <div className="mig-divider-line" />
        </div>
      </div>
    </section>
  )
}

function ReadOnlyFieldRow({
  label,
  value,
  multiline,
}: {
  label: string
  value: string
  multiline?: boolean
}) {
  return (
    <div className="mig-contact-field-row">
      <div className="mig-contact-field-inner" data-multiline={!!multiline}>
        <span className="mig-contact-field-label">{label}</span>
        <span className="mig-contact-field-readonly-value">{value}</span>
      </div>
      <div className="mig-divider-line" />
    </div>
  )
}
